import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { toast } from 'react-toastify';
import { UserAuthContext } from '../../UserAuthContext';
import { addToGuestCart } from '../../utils/guestCart';
import { getProductImageUrl, useProductImageFallback } from '../../utils/productImages';
import BrandLogoImage from '../BrandLogoImage';
import './RelatedProducts.css';

const API_BASE = 'https://valleyapi-dr9y.onrender.com';
const SITE_URL = 'https://www.valleyintlsupply.com';
const PRODUCTS_PER_SLIDE = 4;
const CACHE_TIMEOUT = 5 * 60 * 1000;
const recommendationCache = new Map();

const SECTION_CONFIG = [
  { key: 'related', title: 'Related Products', description: 'Products from the same category and similar industrial applications.' },
  { key: 'sameBrand', title: 'Same Brand', description: 'More products from this manufacturer.' },
  { key: 'inStock', title: 'In Stock', description: 'Available products ready for your next order.' },
  { key: 'brands', title: 'Brands', description: 'Explore industrial products from our manufacturer network.' }
];

function normalizeProducts(values, currentProduct) {
  const currentId = String(currentProduct?.idproducts || '');
  const currentName = String(currentProduct?.product_name || '').toLowerCase();
  const unique = new Map();

  (Array.isArray(values) ? values : []).forEach((item) => {
    const id = String(item.idproducts || '');
    const name = String(item.product_name || '').toLowerCase();
    if (!item.product_name || id === currentId || name === currentName) return;
    unique.set(id || name, {
      ...item,
      product_stock: Number(item.product_stock) || 0,
      product_image: getProductImageUrl(item.product_image)
    });
  });

  return [...unique.values()].slice(0, 12);
}

function normalizeBrands(values, currentBrand) {
  const unique = new Map();
  (Array.isArray(values) ? values : []).forEach((brand) => {
    const name = String(brand.brand_name || '').trim();
    if (!name) return;
    unique.set(name.toLowerCase(), { ...brand, brand_name: name });
  });

  return [...unique.values()]
    .sort((left, right) => {
      const leftCurrent = left.brand_name.toLowerCase() === String(currentBrand || '').toLowerCase();
      const rightCurrent = right.brand_name.toLowerCase() === String(currentBrand || '').toLowerCase();
      if (leftCurrent !== rightCurrent) return leftCurrent ? -1 : 1;
      return left.brand_name.localeCompare(right.brand_name);
    })
    .slice(0, 12);
}

async function fetchProducts(url, signal) {
  try {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return Array.isArray(data) ? data : (Array.isArray(data.products) ? data.products : []);
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    console.error(`Unable to load recommendations from ${url}:`, error);
    return [];
  }
}

export default function RelatedProducts({ product }) {
  const [sections, setSections] = useState({ related: [], sameBrand: [], inStock: [], brands: [] });
  const [slides, setSlides] = useState({ related: 0, sameBrand: 0, inStock: 0, brands: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(new Set());
  const { user } = useContext(UserAuthContext);
  const navigate = useNavigate();
  const productId = product?.idproducts;

  useEffect(() => {
    if (!productId) return undefined;
    const controller = new AbortController();
    const cached = recommendationCache.get(productId);

    if (cached && Date.now() - cached.timestamp < CACHE_TIMEOUT) {
      setSections(cached.sections);
      setSlides({ related: 0, sameBrand: 0, inStock: 0, brands: 0 });
      setLoading(false);
      return () => controller.abort();
    }

    async function loadRecommendations() {
      setLoading(true);
      const relatedUrl = `${API_BASE}/api/products/related-products/${productId}`;
      const sameBrandUrl = `${API_BASE}/api/products/search?q=*&brand=${encodeURIComponent(product.product_brand || '')}&limit=12&sort=rank_asc`;
      const inStockUrl = `${API_BASE}/api/products/search?q=*&inStock=true&limit=12&sort=stock_desc`;
      const brandsUrl = `${API_BASE}/api/brands`;

      try {
        const [related, sameBrand, inStock, brands] = await Promise.all([
          fetchProducts(relatedUrl, controller.signal),
          fetchProducts(sameBrandUrl, controller.signal),
          fetchProducts(inStockUrl, controller.signal),
          fetchProducts(brandsUrl, controller.signal)
        ]);
        const nextSections = {
          related: normalizeProducts(related, product),
          sameBrand: normalizeProducts(sameBrand, product),
          inStock: normalizeProducts(inStock.filter((item) => Number(item.product_stock) > 0), product),
          brands: normalizeBrands(brands, product.product_brand)
        };
        recommendationCache.set(productId, { timestamp: Date.now(), sections: nextSections });
        setSections(nextSections);
        setSlides({ related: 0, sameBrand: 0, inStock: 0, brands: 0 });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Unable to load product recommendations:', error);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadRecommendations();
    return () => controller.abort();
  }, [product, productId]);

  const recommendationSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': SECTION_CONFIG
      .filter(({ key }) => sections[key].length > 0)
      .map(({ key, title }) => {
        const isBrandSection = key === 'brands';
        return {
          '@type': 'ItemList',
          '@id': `${SITE_URL}/products/${encodeURIComponent(product?.product_name || '')}#${key}`,
          name: `${title} for ${product?.product_name || 'this product'}`,
          numberOfItems: sections[key].length,
          itemListElement: sections[key].map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: isBrandSection
              ? (item.idbrand
                ? `${SITE_URL}/brands/${item.idbrand}`
                : `${SITE_URL}/search?q=*&brand=${encodeURIComponent(item.brand_name)}&page=1`)
              : `${SITE_URL}/products/${encodeURIComponent(item.product_name)}`,
            name: isBrandSection ? item.brand_name : item.product_name
          }))
        };
      })
  }), [product?.product_name, sections]);

  useEffect(() => {
    if (!recommendationSchema['@graph'].length) return undefined;
    let script = document.getElementById('valley-product-recommendations-schema');
    if (!script) {
      script = document.createElement('script');
      script.id = 'valley-product-recommendations-schema';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(recommendationSchema);
    return () => document.getElementById('valley-product-recommendations-schema')?.remove();
  }, [recommendationSchema]);

  const changeSlide = (key, direction) => {
    const totalSlides = Math.max(1, Math.ceil(sections[key].length / PRODUCTS_PER_SLIDE));
    setSlides((current) => ({
      ...current,
      [key]: Math.max(0, Math.min(current[key] + direction, totalSlides - 1))
    }));
  };

  const handleAddToCart = async (item) => {
    if (!user) {
      addToGuestCart({ ...item, quantity: 1 });
      toast.success(`Added ${item.product_name || 'product'} to cart!`);
      return;
    }

    const token = localStorage.getItem('user_token');
    if (!token) {
      toast.error('Session expired. Please log in again.');
      // Send the user home and auto-open the header's login panel instead of
      // the registration form (there is no dedicated /login route).
      try { sessionStorage.setItem('valley_open_auth', '1'); } catch { /* storage unavailable */ }
      navigate('/');
      return;
    }

    setLoadingProducts((current) => new Set(current).add(item.idproducts));
    try {
      const response = await fetch(`${API_BASE}/api/cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          idusers: user.userid,
          idproducts: Number.parseInt(item.idproducts, 10),
          product_name: item.product_name,
          product_description: item.product_description || 'No description',
          product_price: Number.parseFloat(item.product_price) || 0,
          product_image: item.product_image?.replace(`${API_BASE}/data/`, ''),
          product_brand: item.product_brand || 'Unknown',
          quantity: 1
        })
      });
      const responseData = await response.json();
      if (!response.ok) throw new Error(responseData.message || 'Could not add this product.');
      toast.success(`Added ${item.product_name} to cart!`);
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      toast.error(error.message || 'Could not add this product.');
    } finally {
      setLoadingProducts((current) => {
        const next = new Set(current);
        next.delete(item.idproducts);
        return next;
      });
    }
  };

  const renderSection = ({ key, title, description }) => {
    const values = sections[key];
    if (!values.length) return null;
    const totalSlides = Math.max(1, Math.ceil(values.length / PRODUCTS_PER_SLIDE));
    const currentSlide = Math.min(slides[key], totalSlides - 1);
    const headingId = `recommendation-${key}`;
    const viewMoreUrl = key === 'sameBrand'
      ? `/search?q=*&brand=${encodeURIComponent(product.product_brand || '')}&page=1`
      : key === 'inStock' ? '/search?q=*&inStock=true&page=1' : '/search?q=*&page=1';

    return (
      <section className="related-products-main-container" aria-labelledby={headingId} key={key}>
        <div className="related-products-heading">
          <div>
            <h2 id={headingId} className="related-products-section-title">{title}</h2>
            <p>{description}</p>
          </div>
          <Link to={viewMoreUrl}>View more</Link>
        </div>
        <div className="related-products-carousel">
          <button className="related-products-carousel-button prev" type="button" onClick={() => changeSlide(key, -1)} disabled={currentSlide === 0} aria-label={`Previous ${title}`}>←</button>
          <div className="related-products-carousel-inner" style={{ transform: `translateX(-${(currentSlide * 100) / totalSlides}%)`, width: `${totalSlides * 100}%` }}>
            {Array.from({ length: totalSlides }, (_, slideIndex) => {
              const slideProducts = values.slice(slideIndex * PRODUCTS_PER_SLIDE, (slideIndex + 1) * PRODUCTS_PER_SLIDE);
              return (
                <div key={slideIndex} className="related-products-carousel-item" style={{ width: `${100 / totalSlides}%` }}>
                  {slideProducts.map((item) => (
                    <article key={item.idproducts || item.product_name} className="related-products-product-card">
                      <Link className="related-products-card-link" to={`/products/${encodeURIComponent(item.product_name)}`} onClick={() => window.scrollTo(0, 0)}>
                        <div className="related-products-card-image-container">
                          <img src={item.product_image} alt={item.product_name} className="related-products-card-image" loading="lazy" onError={useProductImageFallback} />
                          <span className={`related-products-stock-badge ${item.product_stock > 5 ? 'in-stock' : item.product_stock > 0 ? 'low-stock' : 'out-of-stock'}`}>
                            {item.product_stock > 0 ? 'In Stock' : 'On Request'}
                          </span>
                        </div>
                        <h3 className="related-products-card-title">{item.product_name}</h3>
                        <p className="related-products-card-brand">Brand: {item.product_brand || 'Unknown'}</p>
                        {item.product_category && <p className="related-products-card-category">{item.product_category}</p>}
                        <p className="related-products-card-description">
                          {item.product_description ? `${item.product_description.slice(0, 97)}${item.product_description.length > 97 ? '…' : ''}` : 'No description available'}
                        </p>
                        <p className="related-products-card-price">
                          {Number(item.product_price) > 0 ? `$${Number(item.product_price).toFixed(2)}` : 'Request pricing'}
                        </p>
                        <p className={`related-products-card-stock-info ${item.product_stock > 0 ? '' : 'out-of-stock'}`}>
                          {item.product_stock > 0 ? `${item.product_stock} units available` : 'Available on request'}
                        </p>
                      </Link>
                      <div className="related-products-card-actions">
                        <button className="related-products-add-to-cart" type="button" onClick={() => handleAddToCart(item)} disabled={loadingProducts.has(item.idproducts)} aria-label={`Add ${item.product_name} to cart`}>
                          {loadingProducts.has(item.idproducts) ? <span className="related-products-loading-cart"><i className="related-products-cart-spinner" /> Adding…</span> : <><ShoppingCart className="related-products-cart-icon" /> Add to Cart</>}
                        </button>
                      </div>
                    </article>
                  ))}
                  {slideProducts.length < PRODUCTS_PER_SLIDE && Array.from({ length: PRODUCTS_PER_SLIDE - slideProducts.length }, (_, index) => (
                    <div key={`placeholder-${index}`} className="related-products-product-card placeholder" aria-hidden="true" />
                  ))}
                </div>
              );
            })}
          </div>
          <button className="related-products-carousel-button next" type="button" onClick={() => changeSlide(key, 1)} disabled={currentSlide >= totalSlides - 1} aria-label={`Next ${title}`}>→</button>
        </div>
      </section>
    );
  };

  const renderBrandsSection = ({ key, title, description }) => {
    const values = sections[key];
    if (!values.length) return null;
    const totalSlides = Math.max(1, Math.ceil(values.length / PRODUCTS_PER_SLIDE));
    const currentSlide = Math.min(slides[key], totalSlides - 1);
    const headingId = 'recommendation-brands';

    return (
      <section className="related-products-main-container" aria-labelledby={headingId} key={key}>
        <div className="related-products-heading">
          <div>
            <h2 id={headingId} className="related-products-section-title">{title}</h2>
            <p>{description}</p>
          </div>
          <Link to="/brands">View all brands</Link>
        </div>
        <div className="related-products-carousel related-products-brands-carousel">
          <button className="related-products-carousel-button prev" type="button" onClick={() => changeSlide(key, -1)} disabled={currentSlide === 0} aria-label="Previous brands">←</button>
          <div className="related-products-carousel-inner" style={{ transform: `translateX(-${(currentSlide * 100) / totalSlides}%)`, width: `${totalSlides * 100}%` }}>
            {Array.from({ length: totalSlides }, (_, slideIndex) => {
              const slideBrands = values.slice(slideIndex * PRODUCTS_PER_SLIDE, (slideIndex + 1) * PRODUCTS_PER_SLIDE);
              return (
                <div key={slideIndex} className="related-products-carousel-item" style={{ width: `${100 / totalSlides}%` }}>
                  {slideBrands.map((brand) => {
                    const brandUrl = brand.idbrand
                      ? `/brands/${brand.idbrand}`
                      : `/search?q=*&brand=${encodeURIComponent(brand.brand_name)}&page=1`;
                    return (
                      <article key={brand.idbrand || brand.brand_name} className="related-products-product-card related-products-brand-card">
                        <Link className="related-products-brand-link" to={brandUrl}>
                          <div className="related-products-brand-logo-wrap">
                            <span>{brand.brand_name.slice(0, 2).toUpperCase()}</span>
                            <BrandLogoImage brand={brand} className="related-products-brand-logo" />
                          </div>
                          <h3>{brand.brand_name}</h3>
                          <p>{brand.brand_description || `Explore industrial products from ${brand.brand_name}.`}</p>
                          <strong>View brand products →</strong>
                        </Link>
                      </article>
                    );
                  })}
                  {slideBrands.length < PRODUCTS_PER_SLIDE && Array.from({ length: PRODUCTS_PER_SLIDE - slideBrands.length }, (_, index) => (
                    <div key={`brand-placeholder-${index}`} className="related-products-product-card placeholder" aria-hidden="true" />
                  ))}
                </div>
              );
            })}
          </div>
          <button className="related-products-carousel-button next" type="button" onClick={() => changeSlide(key, 1)} disabled={currentSlide >= totalSlides - 1} aria-label="Next brands">→</button>
        </div>
      </section>
    );
  };

  if (loading) {
    return <section className="related-products-main-container" aria-live="polite"><div className="related-products-loading-container"><div className="related-products-spinner" /><p>Loading product recommendations…</p></div></section>;
  }

  return (
    <div className="related-products-sections">
      {SECTION_CONFIG.map((section) => section.key === 'brands' ? renderBrandsSection(section) : renderSection(section))}
    </div>
  );
}
